import type { ParagraphCtaBanner as ParagraphCtaBannerData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphCtaBanner.module.css";

/**
 * Layout 33 - `PARAGRAPH_CTA_BANNER_LAYOUT`: dark CTA banner ze zdjeciem tla.
 *
 * Struktura:
 *   - <img> tla (image) + gradient overlay (::before) - ciemna nakladka po lewej fadująca w prawo
 *   - Inner (max-width 34rem, lewy padding):
 *       - Eyebrow z markiem: mark + text_1 uppercase
 *       - Title h2 (name) - HTML z inline color spans (TinyMCE clear+bold)
 *       - Desc (text) - opis
 *       - Actions row: CTA button + author paragraph (imie strong, przecinek, rola)
 */
export function ParagraphCtaBanner({
  paragraph,
}: {
  paragraph: ParagraphCtaBannerData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: desc,
    button_name: btnLabel,
    button_url: btnUrl,
    image,
    author_name,
    author_role,
    author_phone,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasDesc = desc && desc.trim() !== "";
  const hasBtn = btnLabel && btnLabel.trim() !== "" && btnUrl && btnUrl.trim() !== "";
  const hasAuthorLine = (author_name && author_name.trim() !== "") || (author_role && author_role.trim() !== "");
  const hasPhone = author_phone && author_phone.trim() !== "";
  const hasAuthor = hasAuthorLine || hasPhone;
  const phoneHref = hasPhone ? `tel:${author_phone.replace(/[^+\d]/g, "")}` : null;

  return (
    <section className={styles.banner}>
      {image ? <img src={image} alt="" className={styles.bg} loading="lazy" /> : null}
      <div className={styles.inner}>
        {hasEyebrow ? (
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowMark} aria-hidden="true" />
            {eyebrow}
          </p>
        ) : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasDesc ? <p className={styles.desc}>{desc}</p> : null}
        {(hasBtn || hasAuthor) ? (
          <div className={styles.actions}>
            {hasBtn ? (
              <a href={btnUrl} className={styles.btn}>
                {btnLabel}
              </a>
            ) : null}
            {hasAuthor ? (
              <div className={styles.author}>
                {hasAuthorLine ? (
                  <p className={styles.authorLine}>
                    {author_name ? <strong className={styles.authorName}>{author_name}</strong> : null}
                    {author_name && author_role ? <span className={styles.authorRole}>{`, ${author_role}`}</span> : author_role ? <span className={styles.authorRole}>{author_role}</span> : null}
                  </p>
                ) : null}
                {hasPhone && phoneHref ? (
                  <a href={phoneHref} className={styles.authorPhone}>{author_phone}</a>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
